import React, { useState, useRef, useEffect } from "react";
import {
  Stage,
  Layer,
  Image as KonvaImage,
  Text,
  Rect,
  Transformer,
} from "react-konva";

const CompoundMarkEditor = () => {
  const [imageSrc, setImageSrc] = useState(null);
  const [imageObj, setImageObj] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 200, height: 200 });
  const [imagePos, setImagePos] = useState({ x: 0, y: 0 });
  const [textAlign, setTextAlign] = useState(["left"]);
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  const [selectedNode, setSelectedNode] = useState(null);
  const [textLines, setTextLines] = useState([""]);
  const [fontSizes, setFontSizes] = useState([20]);
  const [fontColors, setFontColors] = useState(["#000000"]);
  const [fontStyles, setFontStyles] = useState(["normal"]);
  const [textPositions, setTextPositions] = useState([{ x: 100, y: 200 }]);

  const STAGE_WIDTH = 300;
  const STAGE_HEIGHT = 300;

  const stageRef = useRef();
  const imageRef = useRef(null);
  const transformerRef = useRef(null);
  const fileInputRef = useRef(null);
  const textInputRef = useRef([]);
  const textRefs = useRef([]);
  const ignoreBlurRef = useRef(false);

  const styles = {
    markContainer: {
      minHeight: "100vh",
      backgroundColor: "#f8f9fa",
      width: "100%",
      boxSizing: "border-box",
    },
    maxWidth: {
      maxWidth: "1200px",
      margin: "0 auto",
      width: "100%",
    },
    header: {
      textAlign: "center",
      marginBottom: "30px",
      padding: "20px 0",
    },
    title: {
      fontSize: "clamp(1.8rem, 4vw, 2.5rem)",
      fontWeight: "700",
      color: "#2c3e50",
      marginBottom: "10px",
      margin: "0 0 10px 0",
    },
    subtitle: {
      fontSize: "clamp(0.9rem, 2.5vw, 1.1rem)",
      color: "#7f8c8d",
      margin: 0,
      fontWeight: "400",
      padding: "0 10px",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: "20px",
    },
    card: {
      backgroundColor: "white",
      borderRadius: "12px",
      boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
      border: "1px solid #e9ecef",
      overflow: "hidden",
      width: "100%",
    },
    cardHeader: {
      padding: "16px 20px",
      borderBottom: "1px solid #e9ecef",
      backgroundColor: "#f8f9fa",
    },
    cardBody: {
      padding: "20px",
    },
    sectionTitle: {
      fontSize: "clamp(1rem, 2.5vw, 1.2rem)",
      fontWeight: "600",
      color: "#2c3e50",
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    uploadArea: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      minHeight: "100px",
      height: "clamp(100px, 15vw, 120px)",
      border: "2px dashed #4A90E2",
      borderRadius: "8px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      backgroundColor: "inherit",
    },
    uploadContent: {
      textAlign: "center",
      backgroundColor: "inherit",
    },
    uploadText: {
      color: "#4A90E2",
      fontWeight: "500",
      fontSize: "clamp(0.9rem, 2.5vw, 1rem)",
      margin: "8px 0 4px 0",
    },
    uploadSubtext: {
      color: "#95a5a6",
      fontSize: "clamp(0.75rem, 2vw, 0.85rem)",
      margin: 0,
      backgroundColor: "inherit",
    },
    btn: {
      padding: "clamp(8px, 2vw, 10px) clamp(16px, 4vw, 20px)",
      borderRadius: "6px",
      fontWeight: "500",
      transition: "all 0.2s ease",
      border: "none",
      cursor: "pointer",
      fontSize: "clamp(0.8rem, 2vw, 0.9rem)",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      minHeight: "40px",
      textAlign: "center",
    },
    btnPrimary: {
      backgroundColor: "#4A90E2",
      color: "white",
    },
    btnSecondary: {
      backgroundColor: "#e9ecef",
      color: "#495057",
    },
    btnDanger: {
      backgroundColor: "#e74c3c",
      color: "white",
    },
    btnSmall: {
      padding: "clamp(6px, 1.5vw, 8px) clamp(10px, 3vw, 12px)",
      fontSize: "clamp(0.7rem, 1.8vw, 0.8rem)",
      minHeight: "32px",
    },
    input: {
      width: "100%",
      padding: "clamp(8px, 2vw, 10px) clamp(10px, 2.5vw, 12px)",
      border: "1px solid #ddd",
      borderRadius: "6px",
      fontSize: "clamp(0.8rem, 2vw, 0.9rem)",
      transition: "border-color 0.2s ease",
      boxSizing: "border-box",
      minHeight: "40px",
    },
    canvas: {
      border: "1px solid #ddd",
      borderRadius: "8px",
      overflow: "hidden",
      backgroundColor: "white",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      maxWidth: "100%",
      height: "auto",
    },
    flexRow: {
      display: "flex",
      gap: "clamp(8px, 2vw, 10px)",
      alignItems: "center",
      flexWrap: "wrap",
    },
    flexCol: {
      display: "flex",
      flexDirection: "column",
      gap: "clamp(12px, 3vw, 16px)",
    },
    textLineCard: {
      backgroundColor: "#f8f9fa",
      borderRadius: "8px",
      padding: "clamp(12px, 3vw, 16px)",
      border: "1px solid #e9ecef",
    },
    label: {
      display: "block",
      fontSize: "clamp(0.75rem, 2vw, 0.85rem)",
      fontWeight: "500",
      color: "#495057",
      marginBottom: "6px",
    },
    alignButtons: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      overflow: "hidden",
      gap: "4px",
    },
    alignButton: {
      padding: "clamp(6px, 2vw, 8px) clamp(8px, 2vw, 12px)",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontSize: "clamp(0.7rem, 2vw, 0.85rem)",
      fontWeight: "500",
      transition: "all 0.2s ease",
      backgroundColor: "white",
      color: "#495057",
      minHeight: "34px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    slider: {
      width: "100%",
      height: "6px",
      backgroundColor: "#e9ecef",
      borderRadius: "3px",
      appearance: "none",
      cursor: "pointer",
    },
    formGroup: {
      marginBottom: "clamp(12px, 3vw, 16px)",
    },
    canvasContainer: {
      display: "flex",
      justifyContent: "center",
      padding: "clamp(15px, 4vw, 20px)",
      backgroundColor: "#f8f9fa",
    },
    actionButtons: {
      display: "flex",
      flexDirection: "column",
      gap: "clamp(8px, 2vw, 10px)",
    },
    gridContainer: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
      gap: "clamp(10px, 3vw, 12px)",
      marginBottom: "clamp(10px, 3vw, 12px)",
    },
    textHeaderRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "10px",
    },
    textButtons: {
      display: "flex",
      gap: "clamp(6px, 1.5vw, 8px)",
      flexWrap: "wrap",
    },
  };

  // 반응형 그리드 처리
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // 데스크톱에서만 2열 그리드 적용
  const responsiveGrid = {
    ...styles.grid,
    gridTemplateColumns: isMobile || isTablet ? "1fr" : "1fr 400px",
    gap: isMobile ? "15px" : "20px",
  };

  // 텍스트 추가/삭제 시 위치 배열도 업데이트
  const addTextLine = () => {
    if (textLines.length < 4) {
      const nextIndex = textLines.length;

      setTextLines((prev) => [...prev, ""]);
      setFontSizes((prev) => [...prev, 20]);
      setFontColors((prev) => [...prev, "#000000"]);
      setFontStyles((prev) => [...prev, "normal"]);
      setTextPositions((prev) => [
        ...prev,
        { x: 100, y: 200 + prev.length * 10 },
      ]);

      setTimeout(() => {
        const ref = textRefs.current[nextIndex];
        const lastInputRef = textInputRef.current[nextIndex];
        if (lastInputRef) lastInputRef.focus();

        if (ref) {
          setSelectedNode(ref);
          if (transformerRef.current) {
            transformerRef.current.nodes([ref]);
            transformerRef.current.getLayer().batchDraw();
          }
        }
      }, 0);
    }
  };

  const removeTextLine = (index) => {
    if (index === undefined || index === null || typeof index === "object") {
      if (textLines.length > 0) {
        setTextLines((prev) => prev.slice(0, -1));
        setFontSizes((prev) => prev.slice(0, -1));
        setFontColors((prev) => prev.slice(0, -1));
        setFontStyles((prev) => prev.slice(0, -1));
        setTextPositions((prev) => prev.slice(0, -1));
      }
      return;
    }

    setTextLines((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);
    setFontSizes((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);
    setFontColors((prev) => [
      ...prev.slice(0, index),
      ...prev.slice(index + 1),
    ]);
    setFontStyles((prev) => [
      ...prev.slice(0, index),
      ...prev.slice(index + 1),
    ]);
    setTextPositions((prev) => [
      ...prev.slice(0, index),
      ...prev.slice(index + 1),
    ]);
  };

  // 텍스트 정렬 변경 시 x값만 초기화하고 y값은 유지
  const handleTextAlignChange = (newAlign, e) => {
    if (!selectedNode) return null;
    if (selectedNode.attrs.hasOwnProperty("image")) return null;

    const ref = selectedNode;
    if (!ref) return;

    const textWidth = ref.getClientRect().width;

    switch (newAlign) {
      case "left":
        ref.setAttrs({ x: 0 });
        break;
      case "center":
        ref.setAttrs({ x: (STAGE_WIDTH - textWidth) / 2 });
        break;
      case "right":
        ref.setAttrs({ x: STAGE_WIDTH - textWidth });
        break;
      default:
        break;
    }

    ref.setAttrs({ align: newAlign });
    ref.getLayer()?.batchDraw();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => setImageSrc(reader.result);
  };

  const handleDownload = () => {
    const currentNode = transformerRef.current?.nodes()[0];
    if (transformerRef.current) {
      transformerRef.current.nodes([]);
      transformerRef.current.getLayer().batchDraw();
    }

    const uri = stageRef.current.toDataURL({
      mimeType: "image/jpeg",
      quality: 1.0,
      width: 300,
      height: 300,
    });

    const a = document.createElement("a");
    a.href = uri;
    a.download = "compound-mark.jpg";
    a.click();

    if (currentNode && transformerRef.current) {
      transformerRef.current.nodes([currentNode]);
      transformerRef.current.getLayer().batchDraw();
    }
  };

  const handleReset = () => {
    setImageSrc(null);
    setImageObj(null);
    setImagePos({ x: 100, y: 50 });
    setTextLines([""]);
    setFontSizes([20]);
    setFontColors(["#000000"]);
    setFontStyles(["normal"]);
    setTextPositions([{ x: 0, y: 230 }]);
    setTextAlign("left");
    setBackgroundColor("#ffffff");
    setSelectedNode(null);

    if (transformerRef.current) {
      transformerRef.current.nodes([]);
      transformerRef.current.getLayer().batchDraw();
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleButtonMouseDown = () => {
    ignoreBlurRef.current = true;
  };


  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!imageSrc) return;

    const img = new Image();
    img.src = imageSrc;
    img.crossOrigin = "anonymous";
    img.onload = () => {
      setImageObj(img);
      const MAX_WIDTH = 250;
      const scale = img.width > MAX_WIDTH ? MAX_WIDTH / img.width : 1;
      const [width, height] = [img.width * scale, img.height * scale];
      setImageSize({ width, height });

      setImagePos({
        x: (STAGE_WIDTH - width) / 2,
        y: (STAGE_HEIGHT - height) / 2,
      });
    };
  }, [imageSrc]);

  useEffect(() => {
    const DELTA = 4;
    const ARROW_KEYS = ["ArrowLeft", "ArrowUp", "ArrowRight", "ArrowDown"];
    const DELETE_KEYS = ["Delete"];

    const handleKeyDown = (e) => {
      const tag = e.target.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        e.target.isContentEditable
      ) {
        return;
      }

      if (
        (selectedNode && ARROW_KEYS.includes(e.key)) ||
        DELETE_KEYS.includes(e.key)
      ) {
        e.preventDefault();
      }

      if (!selectedNode) return;

      const isImageNode = selectedNode?.attrs?.hasOwnProperty("image");

      if (isImageNode) {
        switch (e.key) {
          case "ArrowLeft":
            setImagePos((pos) => ({ ...pos, x: pos.x - DELTA }));
            break;
          case "ArrowUp":
            setImagePos((pos) => ({ ...pos, y: pos.y - DELTA }));
            break;
          case "ArrowRight":
            setImagePos((pos) => ({ ...pos, x: pos.x + DELTA }));
            break;
          case "ArrowDown":
            setImagePos((pos) => ({ ...pos, y: pos.y + DELTA }));
            break;
          case "Delete":
            setImageSrc(null);
            setImageObj(null);
            setImagePos({ x: 100, y: 50 });
            if (transformerRef.current) {
              transformerRef.current.nodes([]);
              transformerRef.current.getLayer().batchDraw();
            }
            setSelectedNode(null);
            break;
          default:
            break;
        }
        return;
      }

      const textIndex = textRefs?.current.findIndex(
        (arr) => arr?._id === selectedNode?._id
      );
      if (textIndex === -1) return;

      const ref = textRefs?.current[textIndex];
      if (!ref) return;

      const { x, y } = ref.attrs;

      switch (e.key) {
        case "ArrowLeft":
          ref.setAttrs({ x: x - DELTA, y });
          break;
        case "ArrowUp":
          ref.setAttrs({ x, y: y - DELTA });
          break;
        case "ArrowRight":
          ref.setAttrs({ x: x + DELTA, y });
          break;
        case "ArrowDown":
          ref.setAttrs({ x, y: y + DELTA });
          break;
        case "Delete":
          removeTextLine(textIndex);
          setSelectedNode(null);

          if (transformerRef.current) {
            transformerRef.current.nodes([]);
            transformerRef.current.getLayer().batchDraw();
          }
          const nextRef =
            textRefs.current[textIndex] || textRefs.current[textIndex - 1];
          if (nextRef) {
            nextRef.scale({ x: 1, y: 1 });
            nextRef.rotation(0);
            nextRef.getLayer().batchDraw();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown, { passive: false });
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [removeTextLine, selectedNode, textLines]);

  useEffect(() => {
    const handleDocumentClick = (e) => {
      const stageContainer = stageRef.current?.container();
      if (!stageContainer) return;

      if (ignoreBlurRef.current) {
        ignoreBlurRef.current = false;
        return;
      }

      if (!stageContainer.contains(e.target)) {
        setSelectedNode(null);
        if (transformerRef.current) {
          transformerRef.current.nodes([]);
          transformerRef.current.getLayer().batchDraw();
        }
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, []);

  return (
    <div style={styles.markContainer} className="markContainer">
      <div style={styles.maxWidth}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>상표 이미지 생성</h1>
          <p style={styles.subtitle}>
            이미지와 텍스트를 조합해서 나만의 상표를 만들어보세요
          </p>
        </div>

        <div style={responsiveGrid}>
          {/* Left Panel - Controls */}
          <div style={styles.flexCol}>
            {/* Image Upload Section */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.sectionTitle}>
                  <span>📁</span>
                  이미지 업로드
                </h3>
              </div>
              <div style={styles.cardBody}>
                <div
                  style={styles.formGroup}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = "#3d82e6";
                    e.target.style.backgroundColor = "#f0f7ff";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = "#4A90E2";
                    e.target.style.backgroundColor = "#f8fbff";
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    id="image-upload"
                  />
                  <label htmlFor="image-upload" style={styles.uploadArea}>
                    <div style={styles.uploadContent}>
                      <div
                        style={{
                          fontSize: "clamp(1.5rem, 4vw, 2rem)",
                          marginBottom: "8px",
                          backgroundColor: "inherit",
                        }}
                      >
                        📤
                      </div>
                      <p style={styles.uploadText}>클릭하여 이미지 선택</p>
                      <p style={styles.uploadSubtext}>PNG, JPG 파일 지원</p>
                    </div>
                  </label>
                </div>

                {imageObj && (
                  <div
                    style={{
                      backgroundColor: "#f8fbff",
                      borderRadius: "8px",
                      padding: "clamp(12px, 3vw, 16px)",
                      border: "1px solid #4A90E2",
                    }}
                  >
                    <div style={styles.flexRow}>
                      <button
                        onMouseDown={handleButtonMouseDown}
                        onClick={() =>
                          setImagePos((pos) => ({
                            ...pos,
                            x: (STAGE_WIDTH - imageSize.width) / 2,
                          }))
                        }
                        style={{
                          ...styles.btn,
                          ...styles.btnPrimary,
                          ...styles.btnSmall,
                          flex: 1,
                        }}
                      >
                        가로 중앙
                      </button>
                      <button
                        onMouseDown={handleButtonMouseDown}
                        onClick={() =>
                          setImagePos((pos) => ({
                            ...pos,
                            y: (STAGE_HEIGHT - imageSize.height) / 2,
                          }))
                        }
                        style={{
                          ...styles.btn,
                          ...styles.btnPrimary,
                          ...styles.btnSmall,
                          flex: 1,
                        }}
                      >
                        세로 중앙
                      </button>
                    </div>

                    <div style={{ marginTop: "12px" }}>
                      <label style={styles.label}>
                        이미지 크기: {imageSize.width.toFixed(0)}px
                      </label>
                      <input
                        type="range"
                        min="50"
                        max="300"
                        value={imageSize.width}
                        onChange={(e) => {
                          const size = parseInt(e.target.value);
                          const newSize = { width: size, height: size };
                          setImageSize(newSize);
                          setImagePos({
                            x: (STAGE_WIDTH - newSize.width) / 2,
                            y: (STAGE_HEIGHT - newSize.height) / 2,
                          });
                          if (transformerRef.current && imageRef.current) {
                            transformerRef.current.nodes([imageRef.current]);
                            transformerRef.current.getLayer().batchDraw();
                          }
                        }}
                        style={styles.slider}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Text Configuration */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.textHeaderRow}>
                  <h3 style={{ ...styles.sectionTitle, margin: 0 }}>
                    <span>✏️</span>
                    텍스트 설정
                  </h3>
                  <div style={styles.textButtons}>
                    <button
                      onClick={addTextLine}
                      disabled={textLines.length >= 4}
                      style={{
                        ...styles.btn,
                        ...styles.btnPrimary,
                        ...styles.btnSmall,
                        opacity: textLines.length >= 4 ? 0.5 : 1,
                      }}
                    >
                      + 추가
                    </button>
                  </div>
                </div>
              </div>
              <div style={styles.cardBody}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>텍스트 정렬</label>
                  <div style={styles.alignButtons}>
                    {[
                      { value: "left", label: "왼쪽" },
                      { value: "center", label: "가운데" },
                      { value: "right", label: "오른쪽" },
                    ].map(({ value, label }) => (
                      <button
                        className="alignButton"
                        key={value}
                        onClick={() => handleTextAlignChange(value)}
                        onMouseDown={handleButtonMouseDown}
                        style={{
                          ...styles.alignButton,
                          backgroundColor: "rgb(74, 144, 226)",
                          color: "white",
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={styles.flexCol}>
                  {textLines.map((text, i) => (
                    <div key={i} style={styles.textLineCard}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "12px",
                          flexWrap: "wrap",
                        }}
                      >
                        <div
                          style={{
                            width: "clamp(20px, 4vw, 24px)",
                            height: "clamp(20px, 4vw, 24px)",
                            backgroundColor: "#4A90E2",
                            color: "white",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "clamp(0.7rem, 1.8vw, 0.8rem)",
                            fontWeight: "600",
                            flexShrink: 0,
                          }}
                        >
                          {i + 1}
                        </div>
                        <span
                          style={{
                            fontSize: "clamp(0.8rem, 2vw, 0.9rem)",
                            fontWeight: "500",
                            color: "#495057",
                          }}
                        >
                          {i + 1}번째 텍스트
                        </span>
                        <button
                          onClick={() => removeTextLine(i)}
                          style={{
                            ...styles.btn,
                            ...styles.btnDanger,
                            ...styles.btnSmall,
                            marginLeft: "auto",
                          }}
                        >
                          - 삭제
                        </button>
                      </div>

                      <div style={styles.formGroup}>
                        <input
                          type="text"
                          placeholder={`${i + 1}번째 텍스트를 입력하세요`}
                          value={text}
                          ref={(el) => (textInputRef.current[i] = el)}
                          onFocus={() => {
                            if (transformerRef.current) {
                              transformerRef.current.nodes([
                                textRefs.current[i],
                              ]);
                              transformerRef.current.getLayer().batchDraw();
                              setSelectedNode(textRefs.current[i]);
                            }
                          }}
                          onChange={(e) => {
                            const clone = [...textLines];
                            clone[i] = e.target.value;
                            setTextLines(clone);
                          }}
                          style={styles.input}
                        />
                      </div>

                      <div style={styles.gridContainer}>
                        <div>
                          <label style={styles.label}>
                            크기: {fontSizes[i]}px
                          </label>
                          <input
                            type="range"
                            min="10"
                            max="60"
                            value={fontSizes[i]}
                            onMouseDown={handleButtonMouseDown}
                            onChange={(e) => {
                              const clone = [...fontSizes];
                              clone[i] = parseInt(e.target.value);
                              setFontSizes(clone);
                            }}
                            style={styles.slider}
                          />
                        </div>
                        <div>
                          <label style={styles.label}>색상</label>
                          <input
                            type="color"
                            value={fontColors[i]}
                            onMouseDown={handleButtonMouseDown}
                            onChange={(e) => {
                              const clone = [...fontColors];
                              clone[i] = e.target.value;
                              setFontColors(clone);
                            }}
                            style={{
                              ...styles.input,
                              height: "clamp(32px, 8vw, 36px)",
                              cursor: "pointer",
                              padding: "4px",
                            }}
                          />
                        </div>
                      </div>

                      <div>
                        <label style={styles.label}>스타일</label>
                        <select
                          value={fontStyles[i]}
                          onMouseDown={handleButtonMouseDown}
                          onChange={(e) => {
                            const clone = [...fontStyles];
                            clone[i] = e.target.value;
                            setFontStyles(clone);
                          }}
                          style={styles.input}
                        >
                          <option value="normal">일반</option>
                          <option value="bold">굵게</option>
                          <option value="italic">기울임</option>
                          <option value="bold italic">굵고 기울임</option>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Background Settings */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.sectionTitle}>
                  <span>🎨</span>
                  배경 설정
                </h3>
              </div>
              <div style={styles.cardBody}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>배경색</label>
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    style={{
                      ...styles.input,
                      height: "clamp(36px, 8vw, 40px)",
                      cursor: "pointer",
                      padding: "4px",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Canvas and Actions */}
          <div style={styles.flexCol}>
            {/* Canvas */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.sectionTitle}>미리보기</h3>
              </div>
              <div style={{ ...styles.canvasContainer, outline: "none" }}>
                <div style={styles.canvas}>
                  <Stage
                    width={STAGE_WIDTH}
                    height={STAGE_HEIGHT}
                    ref={stageRef}
                    scaleX={1}
                    scaleY={1}
                    onMouseDown={(e) => {
                      if (e.target === e.target.getStage()) {
                        setSelectedNode(null);
                        if (transformerRef.current) {
                          transformerRef.current.nodes([]);
                          transformerRef.current.getLayer().batchDraw();
                        }
                      }
                    }}
                  >
                    <Layer>
                      <Rect
                        x={0}
                        y={0}
                        width={STAGE_WIDTH}
                        height={STAGE_HEIGHT}
                        fill={backgroundColor}
                        onClick={() => {
                          setSelectedNode(null);
                          if (transformerRef.current) {
                            transformerRef.current.nodes([]);
                            transformerRef.current.getLayer().batchDraw();
                          }
                        }}
                      />
                      {imageObj && (
                        <KonvaImage
                          image={imageObj}
                          x={imagePos.x}
                          y={imagePos.y}
                          width={imageSize.width}
                          height={imageSize.height}
                          draggable
                          ref={imageRef}
                          onClick={(e) => {
                            const node = imageRef.current;
                            if (transformerRef.current) {
                              transformerRef.current.nodes([node]);
                              transformerRef.current.getLayer().batchDraw();
                              setSelectedNode(node);
                            }
                          }}
                          onDragStart={(e) => {
                            const node = e.target;
                            if (transformerRef.current) {
                              transformerRef.current.nodes([node]);
                              transformerRef.current.getLayer().batchDraw();
                              setSelectedNode(node);
                            }
                          }}
                          onTransformEnd={(e) => {
                            const node = imageRef.current;
                            const newWidth = node.width() * node.scaleX();
                            const newHeight = node.height() * node.scaleY();
                            node.scaleX(1);
                            node.scaleY(1);
                            setImageSize({
                              width: newWidth,
                              height: newHeight,
                            });
                          }}
                          onMouseUp={(e) => {
                            const attrs = e.currentTarget.attrs;
                            const [x, y] = [attrs.x, attrs.y];
                            setImagePos({ x, y });
                          }}
                        />
                      )}

                      {textLines.map((text, i) => (
                        <Text
                          key={i}
                          text={text}
                          x={textPositions[i]?.x || 0}
                          y={textPositions[i]?.y || 230 + i * 30}
                          align={textAlign[i]}
                          fontSize={fontSizes[i]}
                          fill={fontColors[i]}
                          fontStyle={fontStyles[i]}
                          draggable
                          ref={(ref) => {
                            const width = ref?.measureSize(text).width;
                            const exceeds = width > STAGE_WIDTH - 20;
                            const newWidth = exceeds ? STAGE_WIDTH - 20 : width;
                            textRefs.current[i] = ref?.setAttrs({
                              ellipsis: exceeds,
                              width: newWidth,
                            });
                          }}
                          onDragStart={(e) => {
                            const node = e.target;
                            if (transformerRef.current) {
                              transformerRef.current.nodes([node]);
                              transformerRef.current.getLayer().batchDraw();
                              setSelectedNode(node);
                            }
                          }}
                          onDragEnd={(e) => {
                            const newPositions = [...textPositions];
                            newPositions[i] = {
                              x: e.target.x(),
                              y: e.target.y(),
                            };
                            setTextPositions(newPositions);
                          }}
                          onClick={(e) => {
                            const node = e.target;
                            if (transformerRef.current) {
                              transformerRef.current.nodes([node]);
                              transformerRef.current.getLayer().batchDraw();
                              setSelectedNode(node);
                            }
                          }}
                        />
                      ))}
                      <Transformer ref={transformerRef} keepRatio={false} />
                    </Layer>
                  </Stage>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.sectionTitle}>작업</h3>
              </div>
              <div style={styles.cardBody}>
                <div style={styles.actionButtons}>
                  <button
                    onClick={handleDownload}
                    style={{
                      ...styles.btn,
                      ...styles.btnPrimary,
                      width: "100%",
                      padding: "clamp(10px, 3vw, 12px) clamp(16px, 4vw, 20px)",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#3d82e6")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "#4A90E2")
                    }
                  >
                    💾 이미지 저장
                  </button>

                  {imageObj && (
                    <button
                      onClick={() => {
                        setImageSrc(null);
                        setImageObj(null);
                        setImagePos({ x: 100, y: 50 });
                        if (transformerRef.current) {
                          transformerRef.current.nodes([]);
                          transformerRef.current.getLayer().batchDraw();
                        }
                      }}
                      style={{
                        ...styles.btn,
                        ...styles.btnDanger,
                        width: "100%",
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.backgroundColor = "#c0392b")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.backgroundColor = "#e74c3c")
                      }
                    >
                      🗑️ 이미지 삭제
                    </button>
                  )}

                  <button
                    onClick={handleReset}
                    style={{
                      ...styles.btn,
                      ...styles.btnSecondary,
                      width: "100%",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#dee2e6")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "#e9ecef")
                    }
                  >
                    🔄 전체 초기화
                  </button>
                </div>

                {selectedNode && (
                  <div
                    style={{
                      marginTop: "clamp(15px, 4vw, 20px)",
                      paddingTop: "clamp(15px, 4vw, 20px)",
                      borderTop: "1px solid #e9ecef",
                    }}
                  >
                    <label style={{ ...styles.label, marginBottom: "10px" }}>
                      레이어 순서
                    </label>
                    <div style={styles.flexRow}>
                      <button
                        onMouseDown={handleButtonMouseDown}
                        onClick={() => {
                          selectedNode.moveUp();
                          selectedNode.getLayer().batchDraw();
                        }}
                        style={{
                          ...styles.btn,
                          ...styles.btnSecondary,
                          flex: 1,
                        }}
                      >
                        앞으로
                      </button>
                      <button
                        onMouseDown={handleButtonMouseDown}
                        onClick={() => {
                          selectedNode.moveDown();
                          selectedNode.getLayer().batchDraw();
                        }}
                        style={{
                          ...styles.btn,
                          ...styles.btnSecondary,
                          flex: 1,
                        }}
                      >
                        뒤로
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        /* 모바일 우선 반응형 스타일 */
        @media (max-width: 480px) {
          .markContainer {
            padding: 10px !important;
          }

          .grid {
            gap: 15px !important;
          }

          .card {
            margin: 0 !important;
          }

          .cardHeader,
          .cardBody {
            padding: 15px !important;
          }

          .flexRow {
            flex-direction: column !important;
            gap: 8px !important;
          }

          .textButtons {
            width: 100% !important;
          }

          .textButtons button {
            flex: 1 !important;
          }

          .alignButtons {
            grid-template-columns: 1fr !important;
            gap: 1px !important;
          }

          .gridContainer {
            grid-template-columns: 1fr !important;
          }
        }

        @media (min-width: 481px) and (max-width: 767px) {
          .markContainer {
            padding: 15px !important;
          }

          .alignButtons {
            grid-template-columns: repeat(3, 1fr) !important;
          }

          .gridContainer {
            grid-template-columns: 1fr 1fr !important;
          }
        }

        @media (min-width: 768px) and (max-width: 1023px) {
          .markContainer {
            padding: 25px !important;
          }

          .grid {
            grid-template-columns: 1fr !important;
            gap: 25px !important;
          }

          .alignButtons {
            grid-template-columns: repeat(3, 1fr) !important;
          }

          .gridContainer {
            grid-template-columns: 1fr 1fr !important;
          }
        }

        @media (min-width: 1024px) {
          .markContainer {
            padding: 70px !important;
          }

          .grid {
            grid-template-columns: 1fr 400px !important;
            gap: 30px !important;
          }

          .alignButtons {
            grid-template-columns: repeat(3, 1fr) !important;
          }

          .gridContainer {
            grid-template-columns: 1fr 1fr !important;
          }
        }

        /* 슬라이더 스타일 */
        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          height: clamp(16px, 4vw, 18px);
          width: clamp(16px, 4vw, 18px);
          border-radius: 50%;
          background: #4a90e2;
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        }

        input[type="range"]::-moz-range-thumb {
          height: clamp(16px, 4vw, 18px);
          width: clamp(16px, 4vw, 18px);
          border-radius: 50%;
          background: #4a90e2;
          cursor: pointer;
          border: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        }

        /* 포커스 스타일 */
        input:focus,
        select:focus {
          outline: none !important;
          border-color: #4a90e2 !important;
          box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2) !important;
        }

        /* 터치 디바이스 최적화 */
        @media (hover: none) and (pointer: coarse) {
          button {
            min-height: 44px !important;
            min-width: 44px !important;
          }

          input[type="range"] {
            height: 44px !important;
          }

          .uploadArea {
            min-height: 120px !important;
          }
        }

        /* 고해상도 디스플레이 최적화 */
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          .canvas {
            image-rendering: -webkit-optimize-contrast !important;
            image-rendering: crisp-edges !important;
          }
        }

        /* 다크모드 미디어쿼리 (선택적) */
        @media (prefers-color-scheme: dark) {
          /* 필요시 다크모드 스타일 추가 */
        }

        /* 애니메이션 감소 사용자 배려 */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        /* 인쇄 최적화 */
        @media print {
          .markContainer {
            background: white !important;
            padding: 0 !important;
          }

          .card {
            box-shadow: none !important;
            border: 1px solid #ccc !important;
          }

          button {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default CompoundMarkEditor;
